import { BrowserRouter } from "react-router-dom";
import { Router } from "./RouterProvider";
import { ToastContainer } from "react-toastify";
import { Capacitor } from "@capacitor/core";

import { FullPageError } from "shared/ui/full-page-error";

import "react-toastify/dist/ReactToastify.css";

import AppUpdateModal from "widgets/check-update-modal";
import { useEffect, useState } from "react";
import Loading from "widgets/loading";
import {
  baseUrl,
  cdnUrl,
  rtBaseUrl,
  ruBaseUrl,
  ruCdnUrl,
  ruRtBaseUrl,
  sircapApi,
} from "shared/api/sircap";
import { App } from "@capacitor/app";
import { IsVelarApp } from "static/types";

import * as Sentry from "@sentry/react";
import TagManager from "react-gtm-module";
import { useDeepLink } from "hooks/useDeepLink";
import axios from "axios";
import { setApiReady } from "shared/api/api-ready";

const Fallback = () => (
  <FullPageError
    error={{ message: "Something went wrong! Please refresh the page." }}
  />
);

// Component to handle deep links (must be inside BrowserRouter)
const DeepLinkHandler = () => {
  useDeepLink();
  return null;
};

export function Provider() {
  const [isModalVisible, setModalVisible] = useState(false);
  const [appStoreUrl, setAppStoreUrl] = useState("");
  const [canSkip, setCanSkip] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const setApiUrls = (country: string) => {
    if (country === "RU") {
      sircapApi.setBaseUrl(ruBaseUrl);
      sircapApi.setRtBaseUrl(ruRtBaseUrl);
      sircapApi.setCdnUrl(ruCdnUrl);
    } else {
      sircapApi.setBaseUrl(baseUrl);
      sircapApi.setRtBaseUrl(rtBaseUrl);
      sircapApi.setCdnUrl(cdnUrl);
    }
  };

  const checkRegion = async () => {
    let tryOther = false;
    try {
      const res = await axios.get("https://ipapi.co/json/", { timeout: 2000 });
      setApiUrls(res.data.country_code);
    } catch (error) {
      console.error("Error ipapi.co:", error);
      // send to sentry
      Sentry.captureException(error);
      tryOther = true;
    }

    if (tryOther) {
      try {
        const res = await axios.get("https://api.ipregistry.co/", {
          params: {
            key: "ira_juhljMf0FeHL8NbiEQue3x5OibNNjx0ySPeN",
          },
          timeout: 2000,
        });
        setApiUrls(res.data.location.country.code);
      } catch (error) {
        console.error("Error ipregistry.co:", error);
        Sentry.captureException(error);
      }
    }
  };

  const checkForUpdate = async () => {
    try {
      const platform = Capacitor.getPlatform();
      if (platform === "ios" || platform === "android") {
        const appVersion = (await App.getInfo()).version;
        if (appVersion) {
          const resp = await sircapApi.appInfo({ timeout: 3000 });
          const data = resp?.data?.data;
          if (data) {
            const platformData = IsVelarApp
              ? platform === "ios"
                ? data.ios_velar
                : data.android_velar
              : platform === "ios"
              ? data.ios
              : data.android;
            const latestVersion = platformData?.lastVersion;
            const appStoreLink = platformData?.appUrl;
            // console.log(appVersion, latestVersion, appStoreLink);
            if (latestVersion && appStoreLink) {
              setAppStoreUrl(appStoreLink);

              const [baseLatest, minorLatest, majorLatest] =
                latestVersion.split(".");
              const [baseCurrent, minorCurrent, majorCurrent] =
                appVersion.split(".");

              const isNew =
                +baseLatest > +baseCurrent ||
                (baseLatest === baseCurrent && +minorLatest > +minorCurrent) ||
                (baseLatest === baseCurrent &&
                  minorLatest === minorCurrent &&
                  +majorLatest > +majorCurrent);

              if (appVersion !== latestVersion && isNew) {
                const currentMinor = appVersion!
                  .split(".")
                  .slice(0, 2)
                  .join(".");
                const latestMinor = latestVersion
                  .split(".")
                  .slice(0, 2)
                  .join(".");
                setCanSkip(currentMinor === latestMinor);
                setModalVisible(true);
              }
            }
          }
        }
      }
    } catch (error) {
      console.error("Error checking for updates:", error);
      Sentry.captureException(error);
      // await sircapApi.handleError({
      //   error: "appInfo",
      //   error_info: JSON.stringify(error),
      // });
    } finally {
      setIsLoading(false);
    }
  };

  const appInit = async () => {
    await checkRegion();
    setApiReady();
    await checkForUpdate();
    setIsLoading(false);
  };

  useEffect(() => {
    appInit();
  }, []);

  useEffect(() => {
    TagManager.initialize({ gtmId: "GTM-KBD8VS89" });
  }, []);

  return (
    // <ZendeskProvider apiKey={"7c07c6a9-7d1b-454d-bf98-ef0d8cdfdde7"}>
    <Sentry.ErrorBoundary fallback={<Fallback />}>
      <BrowserRouter>
        {/* todo: uncomment this when we have deep linking */}
        <DeepLinkHandler />

        <ToastContainer
          hideProgressBar={false}
          closeButton={false}
          pauseOnHover={false}
          pauseOnFocusLoss={false}
          autoClose={3500}
          position={"top-right"}
        />
        <Router />
        {isLoading && <Loading />}
        {/* {!isLoading && !isModalVisible && <Router />} */}
        {isModalVisible && (
          <AppUpdateModal
            canSkip={canSkip}
            onHide={() => setModalVisible(false)}
            onUpdate={() => window.open(appStoreUrl, "_blank")}
          />
        )}
      </BrowserRouter>
    </Sentry.ErrorBoundary>
    // </ZendeskProvider>
  );
}
